import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client'
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs'

import { env, isProd } from '@/config/env'
import i18n from '@/translations'

import { ApolloContextErrorLink } from './auth/ApolloContextErrorLink'
import { ApolloRefreshTokenAuthMiddleware } from './auth/ApolloRefreshTokenAuthMiddleware'
import { ApolloServiceReachabilityLink } from './auth/ApolloServiceReachabilityLink'
import { installCachePersist, restorePersistedCache } from './cachePersist'
import { devGraphqlLink } from './devGraphqlLink'

const link = new UploadHttpLink({
  uri: `${env.API_URL}/graphql`,
})

/**
 * - Circle / User: merge: true avoids one query wiping fields the other query still needs,
 *   which was causing alternating authUser ↔ ShowMaterial_MainOwnerCircle refetches.
 * - Image types: preserve contentUrl when a partial write would blank it; avoid custom
 *   merge on Circle.logo / User.avatar (Apollo References).
 */
const cache = new InMemoryCache({
  possibleTypes: {
    MaterialOptionShared: [
      'MaterialOptionBasic',
      'MaterialOptionConsumable',
      'MaterialOptionAccessory',
    ],
  },
  typePolicies: {
    Circle: {
      merge: true,
    },
    User: {
      merge: true,
    },
    Network: {
      merge: true,
      fields: {
        subscription: {
          merge: true,
        },
      },
    },
    CircleLogo: {
      merge(existing, incoming, { mergeObjects }) {
        if (!incoming) {
          return existing ?? incoming
        }
        if (!existing) {
          return incoming
        }
        const merged = mergeObjects(existing, incoming) as {
          contentUrl?: string | null
        }
        const ex = existing as { contentUrl?: string | null }
        const inc = incoming as { contentUrl?: string | null }
        if (
          (inc.contentUrl === null ||
            inc.contentUrl === undefined ||
            inc.contentUrl === '') &&
          ex.contentUrl
        ) {
          return { ...merged, contentUrl: ex.contentUrl }
        }
        return merged
      },
    },
    CircleCover: {
      merge(existing, incoming, { mergeObjects }) {
        if (!incoming) {
          return existing ?? incoming
        }
        if (!existing) {
          return incoming
        }
        const merged = mergeObjects(existing, incoming) as {
          contentUrl?: string | null
        }
        const ex = existing as { contentUrl?: string | null }
        const inc = incoming as { contentUrl?: string | null }
        if (
          (inc.contentUrl === null ||
            inc.contentUrl === undefined ||
            inc.contentUrl === '') &&
          ex.contentUrl
        ) {
          return { ...merged, contentUrl: ex.contentUrl }
        }
        return merged
      },
    },
    Avatar: {
      merge(existing, incoming, { mergeObjects }) {
        if (!incoming) {
          return existing ?? incoming
        }
        if (!existing) {
          return incoming
        }
        const merged = mergeObjects(existing, incoming) as {
          contentUrl?: string | null
        }
        const ex = existing as { contentUrl?: string | null }
        const inc = incoming as { contentUrl?: string | null }
        if (
          (inc.contentUrl === null ||
            inc.contentUrl === undefined ||
            inc.contentUrl === '') &&
          ex.contentUrl
        ) {
          return { ...merged, contentUrl: ex.contentUrl }
        }
        return merged
      },
    },
    NetworkLogo: {
      merge(existing, incoming, { mergeObjects }) {
        if (!incoming) {
          return existing ?? incoming
        }
        if (!existing) {
          return incoming
        }
        const merged = mergeObjects(existing, incoming) as {
          contentUrl?: string | null
        }
        const ex = existing as { contentUrl?: string | null }
        const inc = incoming as { contentUrl?: string | null }
        if (
          (inc.contentUrl === null ||
            inc.contentUrl === undefined ||
            inc.contentUrl === '') &&
          ex.contentUrl
        ) {
          return { ...merged, contentUrl: ex.contentUrl }
        }
        return merged
      },
    },
  },
})

// Restore the previous in-tab snapshot before the first query so reloads paint
// instantly, then keep it fresh on hide/unload. sessionStorage-backed and
// logout-purged — see ./cachePersist.
restorePersistedCache(cache)
installCachePersist(cache)

export const client = new ApolloClient({
  devtools: {
    enabled: !isProd,
  },
  cache,
  link: ApolloLink.from([
    ...(devGraphqlLink ? [devGraphqlLink] : []),
    ApolloContextErrorLink,
    ApolloServiceReachabilityLink,
    ApolloRefreshTokenAuthMiddleware,
    link,
  ]),
  defaultContext: {
    headers: {
      'Accept-Language': i18n.language,
    },
  },
})
